// Central SEO configuration + Schema.org structured-data builders.
// Additive only — no UI/logic changes. Consumed by the <Seo> component.

// Canonical site origin. This is the public custom domain used for all SEO
// output (canonical tags, Open Graph/Twitter URLs, JSON-LD, sitemap). It is
// intentionally fixed to the production domain so canonical URLs stay correct
// regardless of which host (preview/deploy) actually serves the app.
export const SITE_URL = "https://abay360.com";

export const SITE = {
  name: "ABAY360",
  legalName: "ABAY360",
  shortName: "ABAY360",
  slogan: "Find. Buy. Rent. Invest.",
  description:
    "ABAY360 is Ethiopia's smartest AI-powered real estate platform. Search, buy, rent, sell and invest in verified properties across Addis Ababa, Hawassa, Adama, Bahir Dar and beyond — with AI valuations, insights and fraud checks.",
  locale: "en_US",
  twitter: "@abay360",
  logo: `${SITE_URL}/brand/abay360-logo.png`,
  ogImage: `${SITE_URL}/brand/abay360-logo.png`,
  email: "info@abay360.com",
  phone: "+1 (929) 399-9398",
  address: {
    locality: "Addis Ababa",
    region: "Bole",
    country: "ET",
  },
  sameAs: [
    "https://t.me/abay360",
    "https://facebook.com",
    "https://instagram.com",
    "https://twitter.com",
    "https://linkedin.com",
  ],
};

// Build an absolute canonical URL from a path.
export const absoluteUrl = (path = "") => {
  if (!path) return SITE_URL;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

// ── Schema.org builders ───────────────────────────────────────────────

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  legalName: SITE.legalName,
  url: SITE_URL,
  logo: SITE.logo,
  slogan: SITE.slogan,
  description: SITE.description,
  email: SITE.email,
  telephone: SITE.phone,
  address: {
    "@type": "PostalAddress",
    addressLocality: SITE.address.locality,
    addressRegion: SITE.address.region,
    addressCountry: SITE.address.country,
  },
  sameAs: SITE.sameAs,
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.name,
  url: SITE_URL,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/properties?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

// RealEstateListing schema for a property detail page.
export const realEstateListingSchema = (p, url) => {
  if (!p) return null;
  const images = (p.images || []).map((i) => i?.url).filter(Boolean);
  const schema = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: p.title,
    description: (p.description || SITE.description).slice(0, 300),
    url,
    datePosted: p.created_at || undefined,
    image: images.length ? images : [SITE.ogImage],
    offers: {
      "@type": "Offer",
      price: Number(p.price) || undefined,
      priceCurrency: p.currency || "ETB",
      availability: "https://schema.org/InStock",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: p.address || undefined,
      addressLocality: p.city?.name || p.city || SITE.address.locality,
      addressRegion: p.region?.name || p.region || undefined,
      addressCountry: "ET",
    },
  };
  const numRooms = Number(p.bedrooms);
  if (numRooms > 0) schema.numberOfRooms = numRooms;
  if (p.square_meters) {
    schema.floorSize = { "@type": "QuantitativeValue", value: Number(p.square_meters), unitCode: "MTK" };
  }
  if (p.latitude && p.longitude) {
    schema.geo = { "@type": "GeoCoordinates", latitude: p.latitude, longitude: p.longitude };
  }
  return schema;
};

// BreadcrumbList schema from an ordered list of { name, path } items.
export const breadcrumbSchema = (items = []) => {
  if (!items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: absoluteUrl(it.path),
    })),
  };
};
